import React, { useState, useEffect } from 'react';
import {
  Save, Loader2, AlertCircle, ChevronRight, ChevronLeft, X,
  Info, Layers, AlertTriangle, ShieldCheck
} from 'lucide-react';

import { useNavigate, useParams } from 'react-router-dom';
import { useToast, ToastContainer } from '../common/Toast';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import FormStepper from '../common/FormStepper';
import PreContractService from '../../services/preContractService';


const STEPS = [
  { id: 0, label: 'Basic Info', icon: Info },
  { id: 1, label: 'Rule Setup', icon: Layers },
  { id: 2, label: 'Conditions', icon: AlertTriangle },
  { id: 3, label: 'Preview', icon: ShieldCheck },
];

//////////////////////////////////////////////////////////////
// STYLES 
//////////////////////////////////////////////////////////////

const styles = {
  pageContainer: { padding: '24px' },
  card: {
    background: 'white',
    border: '1px solid var(--border-color)',
    boxShadow: 'var(--shadow-sm)',
    overflow: 'hidden',
  },
  cardBody: { padding: '32px' },
  formGroup: { marginBottom: '24px' },
  formLabel: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: 500,
    fontSize: '14px',
  },
  required: { color: '#dc2626', marginLeft: '4px' },
  textarea: { resize: 'vertical', minHeight: '120px' },
  infoBanner: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px 24px',
    backgroundColor: 'var(--accent-color)',
    border: '1px solid var(--border-color)',
    marginBottom: '24px',
  },
  spinning: {
    animation: 'spin 1s linear infinite',
  },
};

//////////////////////////////////////////////////////////////
// COMPONENT
//////////////////////////////////////////////////////////////

const CreatePolicy = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const { success, error: showError, toasts, removeToast } = useToast();

  const [currentStep, setCurrentStep] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'Value Threshold',
    enforcement: 'Critical Block',
    thresholdValue: '',
    currency: 'USD',
    isActive: true,
    appliesTo: 'All Contract Types'
  });

  //////////////////////////////////////////////////////////////
  // FETCH POLICY
  //////////////////////////////////////////////////////////////

  useEffect(() => {
  const fetchPolicy = async () => {
    if (!isEditMode) return;

    setIsLoading(true);
    try {
      const response = await PreContractService.getPolicy(id);
      if (response?.data) {
        const p = response.data;
        setFormData({
          name: p.name || '',
          description: p.description || '',
          type: p.type || 'Value Threshold',
          enforcement: p.enforcement || 'Critical Block',
          thresholdValue: p.thresholdValue || '',
          currency: p.currency || 'USD',
          isActive: p.isActive ?? true,
          appliesTo: p.appliesTo || 'All Contract Types'
        });
      }
    } catch {
      showError('Failed to load policy details.');
      navigate('/contracts/pre-contract/policies');
    } finally {
      setIsLoading(false);
    }
  };

  fetchPolicy();
}, [id, isEditMode, navigate, showError]);

  //////////////////////////////////////////////////////////////
  // HANDLERS
  //////////////////////////////////////////////////////////////

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.thresholdValue) {
      showError('Please fill in all required fields.');
      return;
    }

    setIsSaving(true);

    try {
      if (isEditMode) {
        await PreContractService.updatePolicy(id, formData);
        success('Policy updated successfully');
      } else {
        await PreContractService.createPolicy(formData);
        success('Policy created successfully');
      }

      navigate('/contracts/pre-contract/policies');
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to save policy.');
    } finally {
      setIsSaving(false);
    }
  };

  //////////////////////////////////////////////////////////////
  // STEP NAVIGATION
  //////////////////////////////////////////////////////////////

  const handleNext = (e) => {
    if (e) e.preventDefault();
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(s => s + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(s => s - 1);
      window.scrollTo(0, 0);
    }
  };

  //////////////////////////////////////////////////////////////
  // STEP RENDERERS
  //////////////////////////////////////////////////////////////

  const renderStep0 = () => (
    <>
      <div style={styles.formGroup}>
        <label style={styles.formLabel}>
          Policy Name <span style={styles.required}>*</span>
        </label>
        <input
          className="form-control"
          name="name"
          value={formData.name}
          onChange={handleChange}
        />
      </div>

      <div style={styles.formGroup}>
        <label style={styles.formLabel}>Description</label>
        <textarea
          className="form-control"
          style={styles.textarea}
          name="description"
          value={formData.description}
          onChange={handleChange}
        />
      </div>
    </>
  );

  const renderStep1 = () => (
    <div style={{ display: 'flex', gap: '20px' }}>
      <div style={{ flex: 1 }}>
        <label style={styles.formLabel}>Rule Type</label>
        <select className="form-control" name="type" value={formData.type} onChange={handleChange}>
          <option>Value Threshold</option>
          <option>Template Check</option>
          <option>Jurisdiction</option>
          <option>Vendor Risk</option>
        </select>
      </div>

      <div style={{ flex: 1 }}>
        <label style={styles.formLabel}>Enforcement</label>
        <select className="form-control" name="enforcement" value={formData.enforcement} onChange={handleChange}>
          <option>Critical Block</option>
          <option>Warning</option>
        </select>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <>
      <div style={styles.formGroup}>
        <label style={styles.formLabel}>
          Threshold <span style={styles.required}>*</span>
        </label>
        <input
          className="form-control"
          name="thresholdValue"
          value={formData.thresholdValue}
          onChange={handleChange}
        />
      </div>

      <div style={styles.formGroup}>
        <label style={styles.formLabel}>Status</label>
        <input
          type="checkbox"
          name="isActive"
          checked={formData.isActive}
          onChange={handleChange}
        />
        <span style={{ marginLeft: '10px' }}>
          {formData.isActive ? 'Active' : 'Inactive'}
        </span>
      </div>
    </>
  );

  const renderStep3 = () => (
    <div style={styles.infoBanner}>
      <div>
        <strong>{formData.name || 'Untitled Policy'}</strong>
        <div>{formData.type} • {formData.enforcement}</div>
      </div>
    </div>
  );

  const stepRenderers = [renderStep0, renderStep1, renderStep2, renderStep3];

  //////////////////////////////////////////////////////////////
  // HEADER ACTIONS
  //////////////////////////////////////////////////////////////

  const headerActions = [
    {
      label: 'Cancel',
      variant: 'outline',
      onClick: () => navigate('/contracts/pre-contract/policies'),
    },
    {
      label: isSaving ? 'Saving...' : (isEditMode ? 'Update Policy' : 'Save Policy'),
      icon: isSaving ? <Loader2 size={18} style={styles.spinning} /> : <Save size={18} />,
      variant: 'primary',
      onClick: handleSave,
    }
  ];

  if (isLoading) return <div className="p-5 text-center">Loading...</div>;

  //////////////////////////////////////////////////////////////
  // UI
  //////////////////////////////////////////////////////////////

  return (
    <>
      <PageBreadcrumb />

      <PageHeader
        title={isEditMode ? 'Edit Policy' : 'Create New Policy'}
        description="Create and configure compliance policy"
        actions={headerActions}
      />

      <div style={styles.pageContainer}>

        <div style={styles.card}>
          <FormStepper
            steps={STEPS}
            currentStep={currentStep}
            onStepClick={(i) => i <= currentStep && setCurrentStep(i)}
          />

          <div style={styles.cardBody}>
            <form onSubmit={currentStep === 3 ? handleSave : handleNext}>
              {stepRenderers[currentStep]()}

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: '40px',
                borderTop: '1px solid var(--border-color)',
                paddingTop: '24px'
              }}>
                <div>
                  {currentStep > 0 && (
                    <button type="button" onClick={handleBack} className="btn btn-secondary">
                      <ChevronLeft size={16} /> Back
                    </button>
                  )}
                </div>

                <div>
                  {currentStep < 3 ? (
                    <button type="submit" className="btn btn-primary">
                      Next <ChevronRight size={16} />
                    </button>
                  ) : (
                    <button type="submit" className="btn btn-primary">
                      {isSaving ? 'Saving...' : 'Save Policy'}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>

        <div style={{ marginTop: '24px' }}>
          <div className="card bg-light border-0 shadow-sm">
            <div className="card-body p-4">
              <h6 className="fw-bold d-flex align-items-center gap-2 mb-3">
                <Info size={18} className="text-primary" />
                Policy Logic Helper
              </h6>

              <p className="small text-secondary mb-3">
                <strong className="text-dark">Critical Blocks:</strong> Contracts that fail this rule
                cannot be sent for signature until modified.
              </p>

              <p className="small text-secondary mb-0">
                <strong className="text-dark">Warnings:</strong> Users will see a compliance flag
                but can proceed with justification.
              </p>
            </div>
          </div>
        </div>

      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default CreatePolicy;